import type {
  BeforeSendFn,
  CaptureResult,
  PostHog,
} from "posthog-js";

import {
  type AnalyticsEventMap,
  type AnalyticsEventName,
  type BaseEventProps,
} from "./types";
import { sanitizeErrorReason, sanitizeEventProps } from "./sanitize";

// posthog-js (~190KB) is loaded lazily after first paint; until then the
// wrappers below no-op (analyticsEnabled stays false).
let posthog: PostHog | null = null;
let analyticsEnabled = false;
let initialized = false;

export function isAnalyticsEnabled(): boolean {
  return analyticsEnabled;
}

function beforeSendSanitizer(
  event: Parameters<BeforeSendFn>[0],
): Parameters<BeforeSendFn>[0] {
  if (!event) {
    return event;
  }

  return {
    ...event,
    properties: sanitizeEventProps(
      event.properties as Record<string, unknown>,
    ) as typeof event.properties,
  };
}

export function initPosthogClient() {
  if (initialized) {
    return;
  }

  initialized = true;

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST;

  if (!apiKey || !apiHost) {
    return;
  }

  const load = () =>
    import("posthog-js").then(({ default: client }) => {
      client.init(apiKey, {
        api_host: apiHost,
        defaults: "2026-01-30",
        capture_pageview: "history_change",
        capture_pageleave: true,
        autocapture: true,
        cross_subdomain_cookie: true,
        rageclick: true,
        capture_dead_clicks: true,
        capture_exceptions: true,
        before_send: [beforeSendSanitizer],
      });
      posthog = client;
      analyticsEnabled = true;
    });

  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => load());
  } else {
    setTimeout(load, 0);
  }
}

export function captureEvent<E extends AnalyticsEventName>(
  eventName: E,
  props: AnalyticsEventMap[E],
): CaptureResult | undefined {
  if (!analyticsEnabled || !posthog) {
    return undefined;
  }

  const sanitizedProps = sanitizeEventProps(props as BaseEventProps);
  return posthog.capture(eventName, sanitizedProps);
}

export function identifyUser({
  distinctId,
  properties,
}: {
  distinctId: string;
  properties: Record<string, unknown>;
}) {
  if (!analyticsEnabled || !posthog || !distinctId) {
    return;
  }

  posthog.identify(distinctId, sanitizeEventProps(properties));
}

export function resetUser() {
  if (!analyticsEnabled || !posthog) {
    return;
  }

  posthog.reset();
}

export function setAnalyticsGroups({
  teamId,
  teamName,
  dashboardId,
  dashboardName,
}: {
  teamId: string | null;
  teamName?: string | null;
  dashboardId: string | null;
  dashboardName?: string | null;
}) {
  if (!analyticsEnabled || !posthog) {
    return;
  }

  posthog.resetGroups();

  if (teamId) {
    posthog.group("team", teamId, {
      name: teamName || undefined,
    });
  }

  if (dashboardId) {
    posthog.group("dashboard", dashboardId, {
      name: dashboardName || undefined,
    });
  }

  posthog.register({
    active_team_id: teamId,
    active_dashboard_id: dashboardId,
  });
}

export { sanitizeErrorReason };
